import { all } from '@go-shared/lib/functional';
import { extractTagsFromProperty } from '@go-shared/lib/template';
import { Property } from '@go-shared/types/property';
import {
  BareSailingTemplate,
  BareTemplate,
  SailingTemplate,
  Template,
  TemplateSourceType,
  BuildSailingDayTemplateOptions,
  BareSimpleTemplate,
  SimpleTemplate,
  SimpleSailingTemplate,
  TemplateTransferTask,
} from '@go-shared/types/template';
import { populate as populatePlan, bare as barePlan } from '@go-client/lib/api/plan';

import { apiDelete, apiGet, apiPost, apiPut } from '.';
import { BareEvent } from '@go-shared/types/event';
import { today } from '@go-shared/lib/time';
import { VenueDisposition } from '@go-shared/types/plan';
import { FeedPage } from '@go-shared/types/feedTemplate';
import { BareContent } from '@go-shared/types/content';

export async function transferTemplate(propertyId: string, task: TemplateTransferTask) {
  return apiPost(`/property/${propertyId}/template-transfer`, task);
}

export async function getDayTemplates(
  propertyId: string,
  sourceTypes?: TemplateSourceType[]
): Promise<BareTemplate[]> {
  let templateTypeParams = sourceTypes ? { types: sourceTypes } : {};
  return apiGet(`/property/${propertyId}/template`, { ...templateTypeParams });
}
export async function getSimpleDayTemplates(
  propertyId: string,
  sourceTypes?: TemplateSourceType[]
): Promise<BareSimpleTemplate[]> {
  let templateTypeParams = sourceTypes ? { types: sourceTypes } : {};
  return apiGet(`/property/${propertyId}/simple-template`, { ...templateTypeParams });
}

export async function getDayTemplate(
  templateId: string,
  sourceTypes?: TemplateSourceType[]
): Promise<BareTemplate> {
  let templateTypeParams = sourceTypes ? { types: sourceTypes } : {};
  return apiGet(`/template/${templateId}`, templateTypeParams);
}

export const updateFeedEditor = async (
  property: Property,
  templateId: string,
  currentPage: FeedPage,
  feedSlotNumber: number
) => {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiPut(
    `/property/${property._id}/feed-editor/template/${templateId}`,
    {
      currentPage,
      templateId,
      feedSlotNumber,
    },
    params
  );
};

export async function exportFeed(
  templateId: string,
  options: { legacy: boolean; lang?: string; hideBackground: boolean } = {
    legacy: false,
    hideBackground: false,
  },
  feedSlotNumber: number
): Promise<string[]> {
  return apiPost(`/export/template/${templateId}/feed`, { options, feedSlotNumber });
}

export async function updateDayTemplate(
  templateId: string,
  patch: Partial<Pick<Template, 'name' | 'tags' | 'dayTheme'>>
): Promise<Template> {
  return apiPut(`/template/${templateId}`, patch);
}

interface TemplateModifyEventParams {
  fromDate: string;
  applyStaffToPlan?: string;
}

export async function createEvent(
  templateId: string,
  event: BareEvent,
  applyStaffToPlan?: boolean
): Promise<BareEvent> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  if (applyStaffToPlan) params.applyStaffToPlan = 'true';

  return apiPost(`/template/${templateId}/event`, event, params);
}

export async function updateEvent(
  templateId: string,
  event: BareEvent,
  applyStaffToPlan?: boolean
): Promise<BareEvent> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  if (applyStaffToPlan) params.applyStaffToPlan = 'true';

  return apiPut(`/template/${templateId}/event/${event._id}`, event, params);
}

export async function deleteEvent(templateId: string, eventId: string): Promise<BareTemplate> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiDelete(`/template/${templateId}/event/${eventId}`, params);
}

export async function updateVenueData(
  templateId: string,
  venueData: VenueDisposition
): Promise<BareTemplate> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiPut(`/template/${templateId}/venue/${venueData.template}`, venueData, params);
}

export async function createContent(
  templateId: string,
  content: BareContent
): Promise<BareContent> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiPost(`/template/${templateId}/content`, content, params);
}

export async function updateContent(
  templateId: string,
  content: BareContent
): Promise<BareContent> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiPut(`/template/${templateId}/content/${content._id}`, content, params);
}

export async function deleteContent(templateId: string, contentId: string): Promise<BareTemplate> {
  let params: TemplateModifyEventParams = {
    fromDate: today(),
  };

  return apiDelete(`/template/${templateId}/content/${contentId}`, params);
}

export async function getSailingTemplates(propertyId: string): Promise<BareSailingTemplate[]> {
  return apiGet(`/property/${propertyId}/sailing-template`);
}

export const cloneSailingTemplate = (
  propertyId: string,
  sourceSailingTemplateId: string,
  options?: BuildSailingDayTemplateOptions
) => {
  return apiPost(
    `/property/${propertyId}/sailing-template/${sourceSailingTemplateId}/clone`,
    options
  );
};

export const editSailingTemplate = (
  propertyId: string,
  sailingTemplateId: string,
  newTemplate: BareTemplate
) => {
  return apiPut(`/property/${propertyId}/sailing-template/${sailingTemplateId}`, newTemplate);
};

export const deleteTemplate = (propertyId: string, templateId: string): Promise<void> => {
  return apiDelete(`/property/${propertyId}/template/${templateId}`);
};

export const deleteSailingTemplate = (
  propertyId: string,
  sailingTemplateId: string
): Promise<void> => {
  return apiDelete(`/property/${propertyId}/sailing-template/${sailingTemplateId}`);
};

export function populate(
  bst: BareSailingTemplate,
  property: Property,
  templates: BareTemplate[]
): SailingTemplate {
  let dayTemplates = bst.dayTemplates.map(templateId => templates.find(t => t._id === templateId));

  if (!all(dayTemplates, t => t !== undefined)) {
    throw new Error(`Could not find day template in ${bst.dayTemplates}`);
  }

  // We can cast dayTemplates to Template[] because of the previous check
  return {
    ...bst,
    property,
    dayTemplates: (dayTemplates as BareTemplate[]).map(dt => populateDayTemplate(dt, property)),
  };
}

export function populateSimple(
  bst: BareSailingTemplate,
  property: Property,
  templates: BareSimpleTemplate[]
): SimpleSailingTemplate {
  let dayTemplates = bst.dayTemplates.map(templateId => templates.find(t => t._id === templateId));

  if (!all(dayTemplates, t => t !== undefined)) {
    throw new Error(`Could not find day template in ${bst.dayTemplates}`);
  }

  // We can cast dayTemplates to SimpleTemplate[] because of the previous check
  return {
    ...bst,
    property,
    dayTemplates: (dayTemplates as BareSimpleTemplate[]).map(dt =>
      populateSimpleDayTemplate(dt, property)
    ),
  };
}

export function populateDayTemplate(template: BareTemplate, property: Property): Template {
  const tags = extractTagsFromProperty(template.tags, property);

  return {
    ...template,
    plan: populatePlan(template.plan, property),
    property,
    tags,
  };
}

export function populateSimpleDayTemplate(
  template: BareSimpleTemplate,
  property: Property
): SimpleTemplate {
  const tags = extractTagsFromProperty(template.tags, property);

  return {
    ...template,
    property,
    tags,
  };
}

export function bareDayTemplate(template: Template): BareTemplate {
  return {
    ...template,
    property: template.property._id,
    plan: barePlan(template.plan),
    tags: template.tags.map(t => t._id),
  };
}

export function bareSailingTemplate(sailingTemplate: SailingTemplate): BareSailingTemplate {
  return {
    ...sailingTemplate,
    property: sailingTemplate.property._id,
    dayTemplates: sailingTemplate.dayTemplates.map(dt => dt._id),
  };
}
